import React, { useState, useRef, useEffect } from "react";
import { X, Trash2, Edit, Check } from "lucide-react";
import { Role } from "../../api/services/roles/type";

interface RoleSelectionModalProps {
  roles: Role[];
  onSelectRole: (role: Role) => void;
  onDeleteRole: (roleId: string) => void;
  onCancel: () => void;
  isDeleting?: boolean;
}

export const RoleSelectionModal: React.FC<RoleSelectionModalProps> = ({
  roles,
  onSelectRole,
  onDeleteRole,
  onCancel,
  isDeleting = false,
}) => {
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const buttonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});

  const handleDeleteClick = (roleId: string, event: React.MouseEvent) => {
    const role = roles.find((r) => r.id === roleId);
    if (role?.isSystemRole) return;

    const button = event.currentTarget as HTMLButtonElement;
    const rect = button.getBoundingClientRect();

    setPopoverPosition({
      top: rect.top + window.scrollY,
      left: rect.left - 200,
    });

    setConfirmingDelete(roleId);
  };

  const handleConfirmDelete = (roleId: string) => {
    onDeleteRole(roleId);
    setConfirmingDelete(null);
  };

  const handleCancelDelete = () => {
    setConfirmingDelete(null);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (confirmingDelete && !event.target) return;

      const target = event.target as Element;
      const popover = document.getElementById("delete-popover");
      const deleteButton = buttonRefs.current[confirmingDelete || ""];

      if (
        popover &&
        !popover.contains(target) &&
        !deleteButton?.contains(target)
      ) {
        setConfirmingDelete(null);
      }
    };

    if (confirmingDelete) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [confirmingDelete]);

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-slate-800 rounded-lg w-full max-w-md max-h-[80vh] overflow-y-auto">
          <div className="sticky top-0 bg-slate-800 p-6 border-b border-slate-700 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-white">
              Edit Roles and Permission
            </h2>
            <button
              onClick={onCancel}
              className="text-slate-400 hover:text-white transition-colors"
              disabled={isDeleting}
            >
              <X size={24} />
            </button>
          </div>

          <div className="p-6">
            <div className="text-slate-300 mb-6">
              Please fill in customer basic details to get started.
            </div>

            <div className="space-y-2">
              <label className="block text-slate-300 text-sm font-medium mb-4">
                Title of Role
              </label>

              {roles.map((role) => (
                <div
                  key={role.id}
                  className="flex items-center justify-between p-3 bg-slate-700 rounded-lg border border-slate-600"
                >
                  <span className="text-white font-medium">{role.name}</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onSelectRole(role)}
                      disabled={isDeleting}
                      className="p-2 text-blue-400 hover:text-blue-300 hover:bg-slate-600 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Edit role"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      ref={(el) => {
                        buttonRefs.current[role.id] = el;
                      }}
                      onClick={(e) => handleDeleteClick(role.id, e)}
                      disabled={isDeleting || role.isSystemRole}
                      className="p-2 text-red-400 hover:text-red-300 hover:bg-slate-600 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title={
                        role.isSystemRole
                          ? "Cannot delete system role"
                          : "Delete role"
                      }
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}

              {roles.length === 0 && (
                <div className="text-slate-400 text-center py-8">
                  No roles available
                </div>
              )}
            </div>

            <div className="flex justify-end pt-6 border-t border-slate-700 mt-6">
              <button
                onClick={onCancel}
                disabled={isDeleting}
                className="px-6 py-3 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Popover */}
      {confirmingDelete && (
        <div
          id="delete-popover"
          className="fixed z-[60] bg-slate-800 border border-slate-600 rounded-lg shadow-xl p-3 min-w-[200px]"
          style={{
            top: `${popoverPosition.top}px`,
            left: `${popoverPosition.left}px`,
          }}
        >
          <div className="text-white text-sm font-medium mb-3">
            Delete this role?
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handleConfirmDelete(confirmingDelete)}
              disabled={isDeleting}
              className="flex items-center gap-1 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm rounded transition-colors disabled:opacity-50"
            >
              <Check size={14} />
              Yes
            </button>
            <button
              onClick={handleCancelDelete}
              disabled={isDeleting}
              className="flex items-center gap-1 px-3 py-1.5 bg-slate-600 hover:bg-slate-700 text-white text-sm rounded transition-colors"
            >
              <X size={14} />
              No
            </button>
          </div>
        </div>
      )}
    </>
  );
};
